import React, { useEffect, useState } from "react";
import { Table } from "../common/Table";
import { vehicleLedgerHooks } from "../../hooks/handlers/useVehicleLedgerHandler";
import { TError } from "../../types/common";
import { beautify, formatAmount, numberFormat } from "../../utils/helpers";
import useGet from "../../hooks/common/useGet";
import useAxios from "../../hooks/common/useAxios";
import { Button } from "antd";
import styled from "styled-components";
import {
  Download,
  Search,
  Print,
  SearchOutlined,
  ManageSearchOutlined,
} from "@mui/icons-material";
import FormFields from "../common/FormFields";
import useForm from "../../hooks/common/useForm";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { URL_NEGOTIATED_PAYMENTS } from "../../utils/urls";
import { useModals } from "../../hooks/common/useModal";

interface VehicleLedgerLayoutHandlerProps {
  tabKey: string;
  vehiclePlate?: string;
}

const VehicleLedgerLayoutHandler: React.FC<VehicleLedgerLayoutHandlerProps> = ({
  tabKey,
  vehiclePlate,
}) => {
  const navigate = useNavigate();
  const { form, updateFormValues, updateFormValue, onChange } = useForm();
  const [error, setError] = useState<TError>({});
  const [filters, setFilters] = useState<any>({
    vehicle_plate: vehiclePlate,
  });
  const [hasSearched, setHasSearched] = useState(false);

  const { loadData: getData, data, loading } = useGet();
  const { get } = useAxios();
  const { setCloseVehicleLedger } = useModals();
  const [selectingAll, setSelectingAll] = useState(false);

  const hookConfig = vehicleLedgerHooks[tabKey]
    ? vehicleLedgerHooks[tabKey]({
        form,
        updateFormValue,
        updateFormValues,
        onChange,
        filters,
        setFilters,
        setError,
      })
    : null;

  if (!hookConfig) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        No configuration found for this tab
      </div>
    );
  }

  const {
    searchFields,
    tableColumns,
    tableData,
    tableTitle,
    api,
    options,
    initialFilters,
    exportData,
    printData,
    onSearch,
    searchButtonLabel = "Search",
    selectedInvoices,
    setSelectedInvoices,
  } = hookConfig;

  useEffect(() => {
    if (initialFilters) {
      setFilters((prev: any) => ({ ...prev, ...initialFilters }));
    }
  }, []);

  useEffect(() => {
    if (vehiclePlate) {
      setFilters((prev: any) => ({ ...prev, vehicle_plate: vehiclePlate }));
    }
  }, [vehiclePlate]);

  const handleSearch = async (e: any) => {
    e.preventDefault();
    // A new search is a new result set -- carrying ticks over from the previous
    // vehicle would submit those invoices under the wrong plate.
    setSelectedInvoices?.([]);
    let searchParams = { ...filters, ...form };
    if (onSearch) {
      onSearch(form, (customParams: any) => {
        searchParams = customParams;
      });
    }
    if (api) {
      await getData({
        api,
        params: searchParams,
      });
      setHasSearched(true);
    }
  };

  const handleReset = () => {
    const resetFormValues: any = {};
    if (searchFields && searchFields.length > 0) {
      searchFields.forEach((field: any) => {
        resetFormValues[field.field_name] =
          field.type === "date" ? undefined : "";
      });
    }
    updateFormValues(resetFormValues);
    setFilters(initialFilters || {});
    setError({});
    setHasSearched(false);
  };

  const onFilterChange = async (newFilters: any) => {
    if (api && hasSearched) {
      const searchParams = { ...filters, ...form, ...newFilters };
      await getData({
        api,
        params: searchParams,
      });
      setFilters((prev: any) => ({ ...prev, ...newFilters }));
    }
  };

  const handleProcessInvoices = () => {
    if (selectedInvoices?.length === 0) {
      toast.error("Please select at least one invoice.");
      return;
    }
    const carNumber = selectedInvoices?.[0]?.car_number || "";
    const invoicesToProcess = selectedInvoices?.map((invoice) => ({
      amount: invoice.amount,
      amount_paid: invoice.amount_paid,
      balance: invoice.balance,
      car_number: invoice.car_number,
      car_type_name: invoice.car_type_name,
      created_by: invoice.created_by,
      days_past_due: invoice.days_past_due,
      district_name: invoice.district_name,
      exempted: invoice.exempted,
      generated_at: invoice.generated_at,
      id: invoice.id,
      index: invoice.index,
      invoice_id: invoice.invoice_id,
      invoice_number: invoice.invoice_number,
      number_of_coupons: invoice.number_of_coupons,
      parking_name: invoice.parking_name,
      payment_status: invoice.payment_status?.props?.status ?? "unknown",
      penalty_amount: invoice.penalty_amount,
      phone_number: invoice.phone_number,
      street_name: invoice.street_name,
      surcharge_amount: invoice.surcharge_amount,
      time_spent: invoice.time_spent,
      total_due: invoice.total_due,
      type: invoice.type,
    }));
    navigate(`${URL_NEGOTIATED_PAYMENTS}/create`, {
      state: { selectedInvoices: invoicesToProcess, carNumber: carNumber },
    });
    setCloseVehicleLedger();
    handleReset();
  };

  const tableDataResults = data?.results || data || [];
  const totalCount = data?.count || tableDataResults?.length || 0;
  const mappedRows = tableData ? tableData(tableDataResults) : [];
  const pageRows = Array.isArray(mappedRows) ? mappedRows : [];

  const selectedCount = selectedInvoices?.length || 0;
  const wholePageSelected =
    pageRows.length > 0 &&
    pageRows.every((row: any) =>
      selectedInvoices?.some((item: any) => item.id === row.id),
    );
  // Only worth offering once the visible page is exhausted and more remain.
  const canSelectAcrossPages =
    !!setSelectedInvoices &&
    wholePageSelected &&
    totalCount > pageRows.length &&
    selectedCount < totalCount;

  // Pulls the full result set in one request so selection can span every page.
  const handleSelectAcrossPages = async () => {
    if (!api || !setSelectedInvoices) return;
    setSelectingAll(true);
    await get({
      api,
      params: { ...filters, ...form, page: 1, page_size: totalCount },
      successHandler: (allData: any) => {
        const results = allData?.results || allData || [];
        const mappedAll = tableData ? tableData(results) : results;
        const allRows = Array.isArray(mappedAll) ? mappedAll : [];
        setSelectedInvoices(allRows);
        if (allRows.length < totalCount) {
          toast(`Selected ${allRows.length} of ${totalCount} invoices.`);
        }
      },
      errorHandler: () =>
        toast.error("Could not load all invoices. Please try again."),
    });
    setSelectingAll(false);
  };

  const selectedAmount =
    selectedInvoices?.reduce(
      (total: number, invoice: any) =>
        total + parseFloat(invoice.total_due || 0),
      0,
    ) || 0;

  return (
    <Wrapper>
      {searchFields && searchFields.length > 0 && (
        <SearchPanel onSubmit={handleSearch}>
          <div className="panel-head">
            <SearchOutlined fontSize="small" />
            <span>Search criteria</span>
          </div>
          <div className="panel-body">
            <FormFields
              fields={searchFields}
              error={error}
              form={form}
              onChange={onChange}
              options={options}
              updateFormValue={updateFormValue}
              updateFormValues={updateFormValues}
              setError={setError}
            />
          </div>
          <div className="panel-actions">
            <Button htmlType="button" onClick={handleReset}>
              Reset
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              icon={<Search fontSize="small" />}
            >
              {searchButtonLabel}
            </Button>
          </div>
        </SearchPanel>
      )}

      {!hasSearched && (
        <EmptyHint>
          <ManageSearchOutlined />
          <strong>No search yet</strong>
          <span>Fill in the search criteria above and select Search.</span>
        </EmptyHint>
      )}

      {hasSearched && tableColumns && tableData && (
        <ResultsWrapper>
          {selectedCount > 0 && (
            <SelectionBar>
              <span className="count">
                <strong>{selectedCount}</strong> of {totalCount} invoice
                {totalCount === 1 ? "" : "s"} selected
              </span>
              {canSelectAcrossPages && (
                <Button
                  type="link"
                  size="small"
                  loading={selectingAll}
                  onClick={handleSelectAcrossPages}
                >
                  Select all {totalCount} across pages
                </Button>
              )}
              {setSelectedInvoices && (
                <Button
                  type="link"
                  size="small"
                  danger
                  onClick={() => setSelectedInvoices([])}
                >
                  Clear selection
                </Button>
              )}
            </SelectionBar>
          )}
          <Table
            loading={loading}
            count={totalCount}
            tableTitle={
              `Car Type: ${form.car_type_name || "Not Specified"}` ||
              tableTitle ||
              `${beautify(tabKey)} Results`
            }
            tableColumns={tableColumns ? tableColumns(tableDataResults) : []}
            tableData={pageRows}
            filters={filters}
            onFilterChange={onFilterChange}
            tableHeaderItem={
              <div style={{ display: "flex", gap: "8px" }}>
                {printData && (
                  <Button
                    type="default"
                    icon={<Print fontSize="small" />}
                    onClick={() => console.log("Print data", printData)}
                  >
                    Print
                  </Button>
                )}
                {exportData && (
                  <Button
                    type="primary"
                    icon={<Download fontSize="small" />}
                    onClick={() => console.log("Export data", exportData)}
                  >
                    Export
                  </Button>
                )}
                {selectedInvoices?.length > 0 && (
                  <Button type="primary" onClick={handleProcessInvoices}>
                    Request for Payment
                  </Button>
                )}
              </div>
            }
          />
          {(totalCount > 0 || selectedCount > 0) && (
            <Totals>
              {totalCount > 0 && (
                <div className="total">
                  <span className="label">Total results</span>
                  <span className="value">{numberFormat(totalCount)}</span>
                </div>
              )}
              {selectedCount > 0 && (
                <div className="total">
                  <span className="label">Selected invoices amount</span>
                  <span className="value">{formatAmount(selectedAmount)}</span>
                </div>
              )}
            </Totals>
          )}
        </ResultsWrapper>
      )}
    </Wrapper>
  );
};

export default VehicleLedgerLayoutHandler;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SearchPanel = styled.form`
  background: var(--lp-raised);
  border: 1px solid var(--lp-border);
  border-radius: var(--lp-r-md);
  box-shadow: var(--lp-shadow-sm);

  .panel-head {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 18px;
    border-bottom: 1px solid var(--lp-border);
    font-size: 13px;
    font-weight: 700;
    color: var(--lp-text);

    svg {
      color: var(--lp-primary);
    }
  }

  .panel-body {
    padding: 16px 18px 0;
  }

  .panel-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 0 18px 16px;
  }
`;

const EmptyHint = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 36px 16px;
  border: 1px dashed var(--lp-border);
  border-radius: var(--lp-r-md);
  text-align: center;

  svg {
    margin-bottom: 4px;
    font-size: 32px;
    color: var(--lp-primary);
    opacity: 0.6;
  }

  strong {
    font-size: 14px;
    color: var(--lp-text);
  }

  span {
    font-size: 13px;
    color: var(--lp-muted);
  }
`;

const ResultsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SelectionBar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  padding: 8px 16px;
  border: 1px solid var(--lp-primary-line);
  border-radius: var(--lp-r-sm);
  background: var(--lp-primary-soft);
  font-size: 13px;
  color: var(--lp-text);

  .count {
    margin-right: 8px;
  }
`;

const Totals = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;

  .total {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 160px;
    padding: 10px 14px;
    border-radius: var(--lp-r-sm);
    background: var(--lp-tile);
  }

  .label {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--lp-muted);
  }

  .value {
    font-size: 15px;
    font-weight: 700;
    color: var(--lp-text);
  }
`;
